import Box from 'assets/components/box/Box';
import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import DataMap from 'assets/components/dataMap/DataMap';
import { addLog } from 'assets/components/feedback/Feedback';
import useForm from 'assets/components/form/hooks/Form';
import FileInputSimple from 'assets/components/inputs/file/FileInputSimple';
import TextInput from 'assets/components/inputs/text/TextInput';
import InputWrapper from 'assets/components/inputs/wrappers/InputWrapper';
import DocumentPreview from 'assets/components/other/DocumentPreview';
import useApi from 'assets/hooks/api/useApi';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import { HttpStatus } from 'config/Api.Config';
import claimJobDocumentDeleteApi from 'models/claims/claimJobDocument/delete/Api.ClaimJobDocument.Delete';
import claimJobDocumentSaveApi from 'models/claims/claimJobDocument/save/Api.ClaimJobDocument.Save';
import ClaimTypeDocument from 'models/claims/claimTypeDocument/Model.ClaimTypeDocument';
import ClaimJobStatusCode from 'models/enums/ClaimJobStatusCode';
import ClaimStatusCode from 'models/enums/ClaimStatusCode';
import { Fragment, useMemo } from 'react';
import { useClaimJobEdit } from './ClaimEdit.Job.Hooks';

type DeleteJobDocumentProps = { documentId: number; claimJobId: number };
function OneDocForm(props: { claimTypeDocument: ClaimTypeDocument }) {
  const { lang } = getLocales();
  const { reload, claim, uploadedDocuments, claimJob, permissions, isClaimAdmin, isClaimOwner } = useClaimJobEdit();

  const isClaimInCorrectStatus =
    claim?.statusCode === ClaimStatusCode.OPEN_ACTIVE_IN_PROGRESS ||
    claim?.statusCode === ClaimStatusCode.OPEN_ACTIVE_SUBMITTED_CUSTOMER ||
    claim?.statusCode === ClaimStatusCode.OPEN_ACTIVE_SUBMITTED_DEALERSHIP ||
    claim?.statusCode === ClaimStatusCode.OPEN_ACTIVE_SUBMITTED_OTHER ||
    claim?.statusCode === ClaimStatusCode.OPEN_ACTIVE_SUBMITTED_REPAIRFACILITY ||
    claim?.statusCode === ClaimStatusCode.OPEN_ACTIVE_SUBMITTED_SELLING_DEALER;
  const isJobInCorrectStatus =
    isClaimInCorrectStatus && claimJob?.statusCode === ClaimJobStatusCode.OPEN_ACTIVE_IN_PROGRESS;

  const isAlreadySubmitted = claimJob?.claimJobDocuments?.some(
    (cjd) => cjd.claimTypeDocumentId === props.claimTypeDocument.id
  );

  const docForm = useForm<{
    claimJobId: number;
    claimTypeDocumentId: number;
    documents: Array<Utils.FormData<Omit<Model.IClaimJobDocument, 'claimTypeDocumentId'>>>;
  }>({
    default: {
      claimJobId: claimJob.id,
      claimTypeDocumentId: props.claimTypeDocument.id,
      documents: [],
    },
    onSubmit: async ({ claimJobId, claimTypeDocumentId, documents }) => {
      for (const document of documents) {
        if (document.formFile) {
          const res = await claimJobDocumentSaveApi({
            ...document,
            claimTypeDocumentId,
            claimJobId,
          });
          if (res.status !== HttpStatus.ok) {
            addLog({
              error: 'We were unable to upload document',
            });
          }
        }
      }
      docForm.reload();
      reload();
    },
    validation: (data, errors) => {
      if (data.documents?.some((it) => !it.name)) errors.documents = { name: lang.required };
      if (props.claimTypeDocument.required && !isAlreadySubmitted && data.documents?.some((it) => !it.formFile))
        errors.documents = { formFile: { _objectError: lang.required } };
    },
  });

  const uploadedFiles = useMemo(
    () =>
      docForm.data?.documents?.map((doc) => ({
        original: doc.formFile,
        type: doc.formFile.type as Utils.File.MimeType,
        dataUrl: window.URL.createObjectURL(doc.formFile),
        name: doc.name,
        notes: doc.notes,
      })),
    [docForm.data.documents]
  );

  const hasUploadedDocs = uploadedDocuments?.some((it) => it.claimTypeDocumentId === props.claimTypeDocument.id);
  const allowMoreUploads = !hasUploadedDocs ? true : !!props.claimTypeDocument.allowMultiple;
  const isNonEditable =
    !allowMoreUploads ||
    !isJobInCorrectStatus ||
    !(isClaimAdmin || (isClaimOwner && permissions?.UPLOAD_CLAIMJOBDOCUMENT));
  const isDisabled = isNonEditable || docForm.hasErrors;
  return (
    <Box style={{ marginBottom: Theme.Size.L }}>
      <InputWrapper>
        <FileInputSimple
          name="files"
          value={uploadedFiles}
          onChange={({ files }) => {
            docForm.update({
              documents: [
                ...(docForm.data.documents ?? []),
                ...files.map((file) => ({
                  formFile: file.original,
                  name: file.original?.name,
                  approved: !props.claimTypeDocument.approvalRequired,
                })),
              ],
            });
          }}
          fileLimit={allowMoreUploads ? 5 : 1}
          label={props.claimTypeDocument.name}
          error={allowMoreUploads ? undefined : docForm.errors?.documents?.formFile?._objectError}
          disabled={isNonEditable}
        />
        <Button
          render={lang.upload}
          onClick={docForm.submit}
          disabled={isDisabled}
          active={!docForm.hasErrors}
          class={ThemeButtonCircle}
          style={{ flexShrink: 0 }}
        />
      </InputWrapper>
      <DataMap
        data={uploadedFiles}
        render={({ data: uploadedFile, index }) => (
          <Fragment key={index}>
            <br />
            <br />
            <InputWrapper>
              <TextInput
                name="name"
                label={lang.name}
                value={uploadedFile.name}
                error={!uploadedFile.name ? lang.required : undefined}
                disabled={isNonEditable}
                onChange={(data) => {
                  docForm.data.documents[index] = { ...docForm.data.documents[index], ...data };
                  docForm.update({ documents: [...docForm.data.documents] });
                }}
              />
              {props.claimTypeDocument?.allowNotes && (
                <TextInput
                  name="notes"
                  label={lang.notes}
                  value={uploadedFile.notes}
                  disabled={isNonEditable}
                  onChange={(data) => {
                    docForm.data.documents[index] = { ...docForm.data.documents[index], ...data };
                    docForm.update({ documents: [...docForm.data.documents] });
                  }}
                />
              )}
            </InputWrapper>
          </Fragment>
        )}
      />
    </Box>
  );
}
export default function ClaimEditJobDocuments() {
  const { lang } = getLocales();
  const { reload, claim, claimJob, uploadedDocuments, uploadableDocuments, permissions, isClaimAdmin, isClaimOwner } =
    useClaimJobEdit();

  const isClaimInCorrectStatus =
    claim?.statusCode === ClaimStatusCode.OPEN_ACTIVE_IN_PROGRESS ||
    claim?.statusCode === ClaimStatusCode.OPEN_ACTIVE_SUBMITTED_CUSTOMER ||
    claim?.statusCode === ClaimStatusCode.OPEN_ACTIVE_SUBMITTED_DEALERSHIP ||
    claim?.statusCode === ClaimStatusCode.OPEN_ACTIVE_SUBMITTED_OTHER ||
    claim?.statusCode === ClaimStatusCode.OPEN_ACTIVE_SUBMITTED_REPAIRFACILITY ||
    claim?.statusCode === ClaimStatusCode.OPEN_ACTIVE_SUBMITTED_SELLING_DEALER;
  const isJobInCorrectStatus =
    isClaimInCorrectStatus && claimJob?.statusCode === ClaimJobStatusCode.OPEN_ACTIVE_IN_PROGRESS;

  const { execute: deleteClaimJobDocument } = useApi({
    action: claimJobDocumentDeleteApi,
    wait: true,
    callback: reload,
  });

  const onJobDocumentDelete = ({ documentId, claimJobId }: DeleteJobDocumentProps) => {
    deleteClaimJobDocument({ claimJobId, documentId });
  };

  return !uploadableDocuments.length ? null : (
    <>
      <h3 style={{ display: 'flex', justifyContent: 'space-between' }}>{lang.documents}</h3>
      <br />
      <DataMap
        data={uploadableDocuments}
        render={({ data: doc }) => <OneDocForm key={doc.id} claimTypeDocument={doc} />}
      />
      <br />
      <DocumentPreview
        showGallery={true}
        documents={uploadedDocuments.map((it) => ({
          id: it.id,
          url: it.mediaFile?.url,
          name: it.name,
          extension: it.mediaFile?.extension,
          mimeType: it.mediaFile?.contentType,
          storedFileName: it.mediaFile?.name,
          description: it.notes,
          approved: it.approved,
          documentType: lang.document,
          documentName: uploadableDocuments.find((ud) => ud.id === it.claimTypeDocumentId)?.name,
          onDelete: () => onJobDocumentDelete({ documentId: it.id, claimJobId: it.claimJobId }),
        }))}
        isDeleteDisabled={
          !isJobInCorrectStatus || !(isClaimAdmin || (isClaimOwner && permissions?.DELETE_CLAIMJOBDOCUMENT))
        }
        isDownloadDisabled={!(isClaimAdmin || (isClaimOwner && permissions?.DOWNLOAD_CLAIMJOBDOCUMENT))}
      />
    </>
  );
}
